import React from "react";

/**
 * MukhiSvg — a rudraksha diagram drawn from the number itself, not from an image.
 *
 * Geometry
 *   Draw a circle of radius r. For i in 0..n-1, place a meridian at
 *     θ = ((i + 0.5) / n) * π - π/2
 *   That gives an even distribution across the front hemisphere. Each meridian
 *   is rendered as an ellipse whose x-radius is |sin θ| * r (an arc), and where
 *   that radius is under ~1 pixel the meridian becomes a straight vertical
 *   line — the pole cleft.
 *
 * Because the diagram is generated from the number, a MukhiSvg with n=5 always
 * has exactly five clefts. There is no way for it to teach the wrong count.
 *
 * Use only on guide pages. Never on product listings — see the founder's rule.
 */
export function MukhiSvg({
  n,
  size = 160,
  stroke = "currentColor",
  fill = "none",
  strokeWidth = 1.4,
  ariaLabel,
  className = "",
}) {
  const c = size / 2;
  const r = size * 0.42;
  const meridians = [];
  for (let i = 0; i < n; i += 1) {
    const theta = ((i + 0.5) / n) * Math.PI - Math.PI / 2;
    const rx = Math.abs(Math.sin(theta)) * r;
    if (rx < 0.6) {
      // Meridian passes through the pole — draw a straight vertical line so the
      // count remains visible even when a full arc would collapse to a hairline.
      meridians.push(
        <line
          key={i}
          x1={c}
          y1={c - r}
          x2={c}
          y2={c + r}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />,
      );
    } else {
      meridians.push(
        <ellipse
          key={i}
          cx={c}
          cy={c}
          rx={rx}
          ry={r}
          stroke={stroke}
          strokeWidth={strokeWidth}
          fill={fill}
        />,
      );
    }
  }
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel || `Diagram of a ${n} mukhi rudraksha with ${n} clefts`}
      className={className}
    >
      <title>{`${n} mukhi rudraksha — ${n} clefts, drawn from the number`}</title>
      <circle cx={c} cy={c} r={r} stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
      {meridians}
    </svg>
  );
}

/**
 * MukhiSpecial — the joined forms drawn programmatically from the same rules.
 * Each "kind" arranges two or three MukhiSvg circles side-by-side (or one inside
 * a slightly larger one) so the diagram is still faithful to the count.
 */
export function MukhiSpecial({ kind, size = 200, ariaLabel, className = "" }) {
  const stroke = "currentColor";
  const strokeWidth = 1.4;
  const half = size / 2;

  const single = (cx, cy, n, r) => {
    // Reuse the MukhiSvg generator inline so we can position multiple beads.
    const meridians = [];
    for (let i = 0; i < n; i += 1) {
      const theta = ((i + 0.5) / n) * Math.PI - Math.PI / 2;
      const rx = Math.abs(Math.sin(theta)) * r;
      if (rx < 0.6) {
        meridians.push(
          <line
            key={`m-${cx}-${i}`}
            x1={cx}
            y1={cy - r}
            x2={cx}
            y2={cy + r}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />,
        );
      } else {
        meridians.push(
          <ellipse
            key={`m-${cx}-${i}`}
            cx={cx}
            cy={cy}
            rx={rx}
            ry={r}
            stroke={stroke}
            strokeWidth={strokeWidth}
            fill="none"
          />,
        );
      }
    }
    return (
      <g key={`bead-${cx}-${cy}-${n}`}>
        <circle cx={cx} cy={cy} r={r} stroke={stroke} strokeWidth={strokeWidth} fill="none" />
        {meridians}
      </g>
    );
  };

  let content;
  let title;
  if (kind === "gauri-shankar") {
    // Two naturally fused beads, side by side, joined at the equator.
    const r = size * 0.22;
    content = (
      <>
        {single(half - r * 0.9, half, 5, r)}
        {single(half + r * 0.9, half, 5, r)}
      </>
    );
    title = "Gauri Shankar — two naturally fused rudraksha beads";
  } else if (kind === "ganesh") {
    // A single bead with a natural trunk-like protrusion (rendered as a smaller
    // bump on the upper hemisphere).
    const r = size * 0.34;
    content = (
      <>
        {single(half, half + size * 0.04, 5, r)}
        {single(half + r * 0.55, half - r * 0.75, 3, r * 0.32)}
      </>
    );
    title = "Ganesh mukhi — a natural trunk-like protrusion";
  } else if (kind === "trijuti") {
    // Three fused beads in a triangle.
    const r = size * 0.18;
    content = (
      <>
        {single(half - r * 0.95, half + r * 0.45, 5, r)}
        {single(half + r * 0.95, half + r * 0.45, 5, r)}
        {single(half, half - r * 0.95, 5, r)}
      </>
    );
    title = "Trijuti — three fused rudraksha beads";
  } else if (kind === "savar") {
    // A one-mukhi bead naturally fused to a multi-mukhi (drawn as 5) bead.
    const rL = size * 0.28;
    const rS = size * 0.19;
    content = (
      <>
        {single(half - rL * 0.4, half, 5, rL)}
        {single(half + rL * 0.85, half, 1, rS)}
      </>
    );
    title = "Savar — a one-mukhi bead fused to a multi-mukhi bead";
  } else if (kind === "nandi") {
    // Regional naming: Nandi / Gauri Path — two fused beads with visible pole
    // clefts, similar to Gauri Shankar but tighter and vertically oriented.
    const r = size * 0.22;
    content = (
      <>
        {single(half, half - r * 0.9, 3, r)}
        {single(half, half + r * 0.9, 3, r)}
      </>
    );
    title = "Nandi / Gauri Path — two vertically fused beads (regional naming)";
  } else {
    content = null;
    title = "";
  }

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel || title}
      className={className}
    >
      <title>{title}</title>
      {content}
    </svg>
  );
}
